import React, { useEffect, useMemo, useRef, useState } from "react";

import * as API from "@ekonsilio/api-livechat";
import { Transition } from "@headlessui/react";
import ObjectID from "bson-objectid";
import omit from "lodash/omit";
import { useCookies } from "react-cookie";
import {
    browserName,
    browserVersion,
    getUA,
    osName,
    osVersion,
} from "react-device-detect";
import { useIntl } from "react-intl";
import { useMutation } from "react-query";
import { checkText } from "smile2emoji";

import generateSentAtFromLastMessage from "../../utils/helpers/generateSentAtFromLastMessage";
import { useAddHistoryMutation } from "../../utils/queries/conversations";
import { useSendMessageSyncMutation } from "../../utils/queries/message";
import { useSDK } from "../../utils/sdk";
import { getBehaviorsSessionStorage } from "../../utils/session-storage";
import { useWebmessenger } from "../../utils/webmessenger";
import { useWebSocket } from "../../utils/websocket";
import TextArea from "../TextArea";

const webmessengerApi = new API.Messaging.Webmessenger();
const authenticationApi = new API.Authentication.Auth();

function MessageComposer() {
    const [, setCookie] = useCookies(["eK-global"]);

    const intl = useIntl();
    const {
        token,
        login,
        conversation,
        setConversation,
        setOperator,
        integration,
        operator,
        greeting,
        messages,
        handleSetMessages,
        setTyping,
    } = useWebmessenger();

    const { emit } = useSDK();
    const { websocket, connected } = useWebSocket();

    const textareaRef = useRef();
    const typing = useRef({
        timer: null,
        status: "stop",
    });

    const url = window.location.href;

    const [textareaFocused, setTextareaFocused] = useState(false);
    const [text, setText] = useState("");
    const [typingId, setTypingId] = useState(null);
    const [authenticationIsLoading, setAuthenticationIsLoading] =
        useState(false);
    const [createConversationIsLoading, setCreateConversationIsLoading] =
        useState(false);

    const latestMessage = useMemo(() => {
        if (!messages?.length) return null;
        return messages[messages.length - 1];
    }, [messages]);

    const { mutate: addHistory } = useAddHistoryMutation();

    const {
        mutateAsync: sendSyncMessage,
        isLoading: sendSyncMessageIsLoading,
    } = useSendMessageSyncMutation();

    const { mutate: sendMessage, isLoading: sendMessageIsLoading } =
        useMutation(
            async (message) => {
                if (operator?.type === "agent" && operator?.provider === "n8n")
                    setTyping(true);

                if (!token) {
                    setAuthenticationIsLoading(true);
                    const loginToken = await authenticationApi.register({
                        device: {
                            "user-agent": getUA,
                            os: `${osName} ${osVersion}`,
                            webbrowser: {
                                name: browserName,
                                version: browserVersion,
                            },
                        },
                        location: window.location,
                        integration,
                    });
                    login(loginToken.token);
                    setAuthenticationIsLoading(false);
                }

                const getBehaviorsStore = getBehaviorsSessionStorage();

                if (!conversation || !conversation?._id)
                    setCreateConversationIsLoading(true);

                const tracking = sessionStorage.getItem("eK-tracking");

                const messagePayload = {
                    ...message,
                    author: {
                        type: "visitor",
                    },
                    ...(conversation && conversation._id
                        ? {
                              conversation: conversation._id,
                          }
                        : {
                              url,
                              user: operator._id,
                              avatar: operator.avatar,
                              /**
                               * Send the greetingMessage parameter only if the message was not sent
                               */
                              ...(greeting?.message &&
                                  !getBehaviorsStore.triggered?.greeting
                                      ?.message?.sent && {
                                      greetingMessage: greeting.message,
                                  }),
                              ...(tracking
                                  ? {
                                        tracking: JSON.parse(tracking),
                                    }
                                  : {}),
                          }),
                };

                let msg = null;

                if (operator?.type === "agent" && operator?.provider === "n8n")
                    msg = await sendSyncMessage(messagePayload);
                else msg = await webmessengerApi.sendMessage(messagePayload);

                if (!conversation) {
                    const expires = new Date();
                    expires.setHours(expires.getHours() + 12);

                    setCookie(
                        "eK-global",
                        {
                            maximized: true,
                        },
                        {
                            secure: true,
                            path: "/",
                            sameSite: "strict",
                            expires,
                        }
                    );
                }

                /**
                 * Set the message in the store as sent
                 */
                if (
                    greeting?.message &&
                    !getBehaviorsStore.triggered.greeting?.message?.sent
                ) {
                    getBehaviorsStore.triggered.greeting.message.sent = true;

                    sessionStorage.setItem(
                        "eK-behaviors",
                        JSON.stringify(getBehaviorsStore)
                    );
                }

                return msg;
            },
            {
                onSuccess: (message) => {
                    if (!conversation) {
                        setConversation(message.conversation);
                        setOperator({
                            ...omit(
                                message.conversation.participants.operators.find(
                                    (o) => o.current
                                ),
                                ["current", "startedAt", "endedAt"]
                            ),
                        });

                        addHistory({
                            _id: message.conversation._id,
                            url: window.location.href,
                        });

                        localStorage.setItem(
                            "eK-conversation:new",
                            integration
                        );
                        localStorage.removeItem("eK-conversation:new");

                        emit("conversation:new", {
                            conversation: message.conversation._id,
                        });

                        setCreateConversationIsLoading(false);
                    }

                    if (operator?.type === "agent") {
                        if (operator?.provider === "n8n") setTyping(false);
                        else setTyping(true);
                    }
                },
                onError: (error, data) => {
                    if (
                        operator?.type === "agent" &&
                        operator?.provider === "n8n"
                    )
                        setTyping(false);

                    handleSetMessages((prevMessages) => {
                        const messagesCopy = [...prevMessages];

                        const messageIndex = messagesCopy.findIndex(
                            (message) => message._id === data.messageId
                        );

                        if (messageIndex >= 0) {
                            messagesCopy[messageIndex] = {
                                ...messagesCopy[messageIndex],
                                status: {
                                    ...messagesCopy[messageIndex].status,
                                    error: true,
                                    loading: false,
                                },
                            };
                        }

                        return messagesCopy;
                    });
                },
            }
        );

    /** Handle change on ContentEditable */
    const handleChange = (ev) => {
        /** Convert emoji shortcut on native emoji */
        const newText = checkText(ev.target.value);

        if (conversation) {
            clearTimeout(typing.current.timer);

            typing.current.timer = setTimeout(
                () => {
                    const newTypingId = ObjectID().toHexString();

                    if (!newText) {
                        setTypingId(null);
                    } else if (!typingId) {
                        setTypingId(newTypingId);
                    }

                    websocket.emit("conversation:typing", {
                        conversation: conversation._id,
                        action: newText ? "start" : "stop",
                        message: newText,
                        typingId: typingId || newTypingId,
                    });
                },
                typing.current.status === "stop" ? 0 : 350 // Emit the event on the first typing without delay
            );

            typing.current.status = newText ? "start" : "stop";
        }

        setText(newText);
    };

    /** Handle focus to display send button */
    const handleFocus = () => {
        setTextareaFocused(true);
    };

    /** Handle blur to hide with condition send button */
    const handleBlur = (ev) => {
        /** Only for unit tests */
        if (ev.target.jestContent)
            handleChange({
                ...ev,
                target: {
                    ...ev.target,
                    value: ev.target.jestContent,
                },
            });

        if (text.length === 0) setTextareaFocused(false);
    };

    /** Prepare function for future implmentations */
    const send = () => {
        const sentAt = generateSentAtFromLastMessage(latestMessage);

        if (
            text.trim().length > 0 &&
            !authenticationIsLoading &&
            !createConversationIsLoading
        ) {
            if (conversation && connected) {
                try {
                    clearTimeout(typing.current.timer);
                    websocket.emit("conversation:typing", {
                        conversation: conversation._id,
                        action: "stop",
                        message: "",
                        typingId,
                    });

                    setTypingId(null);
                } catch (err) {
                    // Nothing to do but without eslint error 👀
                }
            }

            const _id = ObjectID().toHexString();

            if (conversation) {
                handleSetMessages((prevState) => [
                    ...prevState,
                    {
                        type: "text",
                        content: text,
                        author: {
                            ...conversation.participants.visitor,
                            type: "visitor",
                        },
                        status: {
                            sentAt,
                            loading: true,
                        },
                        _id,
                    },
                ]);
            }

            sendMessage({
                type: "text",
                content: text,
                messageId: _id,
            });

            setText("");

            textareaRef.current.focus();

            if (
                "Notification" in window &&
                // eslint-disable-next-line
                typeof Notification !== undefined &&
                Notification.permission === "default"
            ) {
                Notification.requestPermission();
            }
        }
    };

    /** Handle key down to detect Enter key and send message */
    const handleKeyDown = (ev) => {
        // send the message if press key enter and only if the message isn't on loading state
        if (!ev.shiftKey && ev.key === "Enter") {
            ev.preventDefault();
            send();
            textareaRef.current.focus();
        }
    };

    useEffect(() => {
        textareaRef?.current?.focus();
    }, [sendMessageIsLoading]);

    useEffect(() => {
        const handleResetTyping = () => {
            if (websocket) {
                websocket.emit("conversation:typing", {
                    conversation: conversation._id,
                    action: "stop",
                    message: "",
                    skipUpdateUserActivity: true,
                    typingId,
                });

                setTypingId(null);
            }

            return "";
        };

        window.addEventListener("beforeunload", handleResetTyping);

        return () => {
            window.removeEventListener("beforeunload", handleResetTyping);
        };
    }, [conversation, websocket]);

    const numberOfRows = (lineHeight) => {
        if (text === "") {
            return 1;
        }
        let height;

        if (textareaRef.current) {
            height = textareaRef.current.scrollHeight;
        }
        if (Math.floor(height / lineHeight) > 3) {
            return 3;
        }
        return Math.floor(height / lineHeight);
    };

    useEffect(() => {
        if (!conversation) {
            setText("");
        }
    }, [conversation]);

    const isInputDisabled =
        (conversation && conversation.status === "closed") ||
        authenticationIsLoading ||
        createConversationIsLoading ||
        sendSyncMessageIsLoading;

    return (
        ((conversation && conversation.status !== "closed") ||
            !conversation) && (
            <div className="ek-chat__writer">
                <div>
                    <TextArea
                        rows={numberOfRows(24)}
                        ref={textareaRef}
                        value={text}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder={intl.formatMessage({
                            id: "WEBMESSENGER.MESSAGE_COMPOSER.PLACEHOLDER",
                            description:
                                "Placeholder : Tapez votre message ici",
                            defaultMessage: "Tapez votre message ici...",
                        })}
                        disabled={isInputDisabled}
                    />
                </div>
                <Transition
                    as="div"
                    show={textareaFocused && text.trim().length > 0}
                    enter="ek-chat__writer--send-enter"
                    leave="ek-chat__writer--send-leave"
                >
                    <button
                        type="button"
                        className="ek-chat__writer--send"
                        disabled={isInputDisabled}
                        onClick={send}
                    >
                        <svg viewBox="0 0 24 24" version="1.1">
                            <g
                                stroke="none"
                                strokeWidth="1"
                                fill="none"
                                fillRule="evenodd"
                            >
                                <path
                                    d="M3,13.5 L19,12 L3,10.5 L3,3.7732928 C3,3.70255344 3.01501031,3.63261921 3.04403925,3.56811047 C3.15735832,3.3162903 3.45336217,3.20401298 3.70518234,3.31733205 L21.9867539,11.5440392 C22.098181,11.5941815 22.1873901,11.6833905 22.2375323,11.7948177 C22.3508514,12.0466378 22.2385741,12.3426417 21.9867539,12.4559608 L3.70518234,20.6826679 C3.64067359,20.7116969 3.57073936,20.7267072 3.5,20.7267072 C3.22385763,20.7267072 3,20.5028496 3,20.2267072 L3,13.5 Z"
                                    fill="currentColor"
                                />
                            </g>
                        </svg>
                    </button>
                </Transition>
            </div>
        )
    );
}

export default MessageComposer;
